'use client';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BadgeIcon from '@mui/icons-material/Badge';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import ContactlessIcon from '@mui/icons-material/Contactless';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import MailIcon from '@mui/icons-material/Mail';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ReportIcon from '@mui/icons-material/Report';
import { DialogContentText, Tooltip } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import enGB from 'date-fns/locale/en-GB';
import { parsePhoneNumber } from 'libphonenumber-js';
import { createFileRoute, Outlet, useLocation, useNavigate, Link } from '@tanstack/react-router';
import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	ConfirmDialog,
	EditableChip,
	GenericDetailPage,
	GroupChip,
	RoleChip,
	Badge,
	Tabs,
	SupervisorChip,
} from '@repo/components';
import { FormDialog } from '@repo/components';
import FaceChip from 'src/components/FaceChip';
import { PERMISSIONS, UserDataSettings, Task, AuthenticatedUser, Profile, User } from '@repo/shared/domain';
import { BlockUserForm, UserForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { toFormattedHex } from 'src/helpers/string';
import { useBreadcrumbLevel, useLocalStorage } from '@repo/components/hooks';
import AddToGroupDialog from 'src/parts/users/AddToGroupDialog';
import BadgeDialog from 'src/parts/users/BadgeDialog';
import MagicCodeDialog from 'src/parts/users/MagicCodeDialog';
import AssignSupervisorDialog from 'src/parts/users/AssignSupervisorDialog';
import { GeneralNotFound } from '@repo/components';
import { getProfile, getUserDataSettings } from 'src/api/admin/users';
import { getUserApprovals } from 'src/api/admin/approvals';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';

// Loaded only when IS_BASF is on; non-BASF tenants never fetch this chunk.
const BasfUserBadgeSlot = lazy(() =>
	import('@nineeng/integration-basf-badge/frontend').then((m) => ({ default: m.BasfUserBadgeSlot })),
);

const formatPhoneNumber = (phone: string) => {
	if (!phone) {
		return '-';
	}
	try {
		return parsePhoneNumber(phone).formatInternational();
	} catch {
		return phone;
	}
};

export const Route = createFileRoute('/_admin/users/$userId')({
	loader: async ({ params }) => {
		const { userId } = params;
		const [profile, userDataSettings] = await Promise.all([getProfile(userId), getUserDataSettings()]);

		return { profile, userDataSettings };
	},
	component: UserDetailLayout,
});

function UserDetailLayout() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { profile: initialProfile, userDataSettings } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const defaultCountry = config?.defaultCountry || 'BE';

	// Use React Query for profile data to enable revalidation
	const { data: profile, refetch: refetchProfile } = useQuery({
		queryKey: ['user-profile', userId],
		queryFn: () => getProfile(userId),
		initialData: initialProfile,
	});

	// Fetch approvals for the user
	const { data: approvals } = useQuery({
		queryKey: ['user-approvals', userId],
		queryFn: () => getUserApprovals(userId),
		initialData: { data: [], count: 0, page: 0, pageSize: 10 },
	});

	const [isUserEditDialogOpen, setUserEditDialogOpen] = useState(false);
	const [isUserDeleteDialogOpen, setIsUserDeleteDialogOpen] = useState(false);
	const [isUserBlockDialogOpen, setIsUserBlockDialogOpen] = useState(false);
	const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
	const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState(false);
	const [is2FADialogOpen, set2FADialogOpen] = useState(false);
	const [isMagicCodeDialogOpen, setIsMagicCodeDialogOpen] = useState(false);
	const [magicCode, setMagicCode] = useState<string | null>(null);
	const [isUserInviteDialogOpen, setIsUserInviteDialogOpen] = useState(false);
	const [isSupervisorDialogOpen, setIsSupervisorDialogOpen] = useState(false);
	const [isBusy, setIsBusy] = useState(false);
	const [isUserInviteTextMessageDialogOpen, setIsUserInviteTextMessageDialogOpen] = useState(false);
	const { value: locallyStoredBackUrl } = useLocalStorage<string>('usersBackUrl');

	const [archiveDate, setArchiveDate] = useState<Date>(
		profile?.user?.plannedArchival?.archiveDate ? new Date(profile.user.plannedArchival.archiveDate) : new Date(),
	);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	useBreadcrumbLevel(0, 'Users', '/users');
	useBreadcrumbLevel(1, `${profile?.user?.firstName}${profile?.user?.lastName ? ` ${profile?.user?.lastName}` : ''}`);

	// Clear document selections in localStorage when user changes
	useEffect(() => {
		localStorage.removeItem('documentTabSelectedRows');
	}, [userId]);

	const goBack = useCallback(() => {
		if (locallyStoredBackUrl) {
			try {
				const url = new URL(locallyStoredBackUrl);
				window.location.href = url.href;
				return;
			} catch {
				// Invalid URL, fall through to default
			}
		}
		navigate({ to: '/users' });
	}, [locallyStoredBackUrl, navigate]);

	const revalidateUserProfile = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
	}, [refetchProfile, queryClient, userId]);

	const revalidateUsersPath = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['users'] });
		queryClient.invalidateQueries({ queryKey: ['in-review-count'] });
		queryClient.invalidateQueries({ queryKey: ['blocked-count'] });
	}, [queryClient]);

	if (!profile || !profile.user) {
		return (
			<GeneralNotFound
				label="We couldn't find this user. Maybe this user has been deleted?"
				action={{
					label: 'Return to users',
					href: '/users',
					variant: 'outlined',
				}}
			/>
		);
	}

	const isBlocked = profile.user?.status === 'blocked';
	const isArchived = profile.user?.status === 'archived';
	const isActive = profile.user?.status === 'active';
	const isPlannedForArchive = !!profile.user?.plannedArchival?.archiveDate;
	const archiveIsValid = archiveDate >= new Date(new Date().setHours(0, 0, 0, 0));

	const faceImage = profile.faceImage;
	const usesRFID = featureFlags.USES_RFID || false;
	const usesRFIDEnc = featureFlags.USES_RFID_ENC || false;
	const usesSupervisor = featureFlags.USES_SUPERVISOR || false;
	const isBasf = featureFlags.IS_BASF || false;
	const showSupervisorAssignment = usesSupervisor && isBasf;

	const extraPropFields = userDataSettings?.settings?.extraUserProps || [];
	const showShortId = userDataSettings?.settings?.showShortId || false;

	const handleExtraPropUpdate = (field: string, value: string) => {
		const extraUserProps = Object.assign({}, profile.user?.userData?.extraUserProps, {
			[field]: value,
		});
		return updateUserData({ extraUserProps });
	};

	const updateUserData = async (userData: Record<string, unknown>) => {
		let updatedUserData: User | undefined;
		try {
			// We need to set the phone to null if it is cleared. Will be an empty string
			if ('phone' in userData) {
				userData.phone = !userData.phone ? null : userData.phone;
			}
			updatedUserData = (await request.put<User>(`/api/users/${profile.user.id}/user-data`, userData)).body;
			revalidateUserProfile();
			setUserEditDialogOpen(false);
		} catch (err: unknown) {
			const error = err as Error;
			EventEmitter.emit('toast', {
				type: 'error',
				message: `Update failed (${error.message})`,
			});
		}
		return updatedUserData;
	};

	const markedForReview = profile.user.markedForReview || [];

	const hasApprovalsTodo = approvals.data.some((t: Task) => t.status === 'todo');
	const hasDocumentsToReview = markedForReview.includes('documents');
	const hasTasksToReview = markedForReview.includes('tasks');
	const isUnderageUser = markedForReview.includes('underage');
	const hasAgeRule = markedForReview.includes('age');
	const hasNationalityRule = markedForReview.includes('nationality');
	const hasScreeningReview = markedForReview.includes('screening');

	// Get current tab from pathname
	const pathSegments = location.pathname.split('/');
	const selectedLayoutSegment = (pathSegments[pathSegments.length - 1] || 'roles') as
		| 'details'
		| 'roles'
		| 'access'
		| 'documents'
		| 'tasks'
		| 'approvals'
		| 'events'
		| 'logs'
		| 'badges';

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		const builtTabs = [
			...(profile?.userForm
				? [
						{
							id: 'details',
							label: 'Details',
						},
					]
				: []),
			{
				id: 'roles',
				label: 'Roles',
			},
			{
				id: 'access',
				label: 'Access',
			},
			...(verifyPermission([PERMISSIONS.adminUserdocumentsView]) ||
			verifyPermission([PERMISSIONS.adminUserdocumentmetadataView])
				? [
						{
							id: 'documents',
							label: 'Documents',
							error: hasDocumentsToReview,
						},
					]
				: []),
			{
				id: 'tasks',
				label: 'Tasks',
				error: hasTasksToReview,
			},
			{
				id: 'approvals',
				label: 'Approvals',
				error: hasApprovalsTodo,
			},
			...(verifyPermission([PERMISSIONS.adminEventsView])
				? [
						{
							id: 'events',
							label: 'Events',
						},
					]
				: []),
			...(verifyPermission([PERMISSIONS.adminUserlogsView])
				? [
						{
							id: 'logs',
							label: 'Logs',
						},
					]
				: []),
			...(usesRFIDEnc && verifyPermission([PERMISSIONS.adminSecurityAccessmanagement])
				? [
						{
							id: 'badges',
							label: 'Badges',
						},
					]
				: []),
		];
		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => {
				return {
					...acc,
					[cur.id]: index,
				};
			},
			{} as Record<string, number>,
		);

		return {
			tabs: builtTabs,
			tabIndexPerTabId,
		};
	}, [profile?.userForm, verifyPermission, hasDocumentsToReview, hasTasksToReview, hasApprovalsTodo, usesRFIDEnc]);

	const handleOpenUserEditDialog = useCallback(() => {
		setUserEditDialogOpen(true);
	}, []);

	const handleCloseUserEditDialog = () => {
		setUserEditDialogOpen(false);
	};

	const inviteUserPrompt = () => {
		setIsUserInviteDialogOpen(true);
	};

	const closeInviteUserPrompt = () => {
		setIsUserInviteDialogOpen(false);
	};

	const archiveUser = async () => {
		try {
			const queryParams = new URLSearchParams();
			queryParams.append('withRoles', 'true');
			if (archiveDate) {
				const dateString = archiveDate.toISOString().split('T')[0];
				queryParams.append('archiveDate', dateString || '');
			}

			const response = await request.del<User>(`/api/users/${profile.user.id}?${queryParams.toString()}`, {});
			await revalidateUsersPath();
			await revalidateUserProfile();
			handleCloseUserDeleteDialog();

			const updatedUser = response?.body;
			const hasPlannedArchival = !!updatedUser?.plannedArchival?.archiveDate;

			if (hasPlannedArchival && updatedUser?.plannedArchival?.archiveDate) {
				EventEmitter.emit('toast', {
					type: 'success',
					message: `User scheduled for archival on ${new Date(updatedUser.plannedArchival.archiveDate).toLocaleDateString('en-GB')}`,
				});
			} else {
				goBack();
			}
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to archive user.',
			});
		}
	};

	const handleCloseBlockUserDialog = () => {
		setIsUserBlockDialogOpen(false);
	};

	const blockUser = async (data: Record<string, unknown>) => {
		try {
			await request.put(`/api/users/${profile.user.id}/block`, data);
			setIsUserBlockDialogOpen(false);
			await revalidateUserProfile();
			revalidateUsersPath();
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to block user.',
			});
		}
	};

	const handleDeleteGroup = async (groupId: string) => {
		await request
			.del(`/api/groups/${groupId}/users/${profile.user.id}`, {})
			.then(() =>
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Company deleted',
				}),
			)
			.catch(() =>
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error deleting company',
				}),
			);

		revalidateUserProfile();
	};

	const handleUpdateBadge = () => {
		setIsBadgeDialogOpen(true);
	};

	const saveBadgeId = async (badgeId: string) => {
		let r;
		try {
			r = await request.put(`/api/users/${profile.user.id}/badge-id`, {
				badgeId,
			});
		} catch (err: unknown) {
			const error = err as Error;
			EventEmitter.emit('toast', {
				type: 'error',
				message: `Update failed (${error.message})`,
			});
		}
		await revalidateUserProfile();
		return r?.body;
	};

	const deleteBadgeId = async () => {
		try {
			await request.put(`/api/users/${profile.user.id}/badge-id`, {
				badgeId: '',
			});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to delete badge id.',
			});
		}
		await revalidateUserProfile();
	};

	const handleCloseBadgeDialog = () => {
		setIsBadgeDialogOpen(false);
	};

	const restoreUser = async () => {
		try {
			await request.put(`/api/users/${profile.user.id}/restore`, {});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to restore user.',
			});
		}

		await revalidateUserProfile();
		revalidateUsersPath();
	};

	const cancelPlannedArchival = async () => {
		try {
			await request.put(`/api/users/${profile.user.id}/cancel-planned-archival`, {});
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Planned archival cancelled successfully.',
			});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to cancel planned archival.',
			});
		}
		setArchiveDate(new Date());
		await revalidateUserProfile();
	};

	const generateMagicCode = async () => {
		try {
			const {
				body: { magicCode },
			} = await request.post<{ magicCode: string }>(`/api/invites/users/${profile.user.id}/magic-code`, {});
			setMagicCode(magicCode);
		} catch (err) {
			console.error(err);
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to generate magic code.',
			});
		}
	};

	const toggleReview = async () => {
		try {
			await request.put(`/api/users/${profile.user.id}/set-review-status`, {
				markForReview: markedForReview.length === 0,
			});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update review status.',
			});
		}
		await revalidateUserProfile();
		revalidateUsersPath();
	};

	const magicCodePrompt = () => {
		setIsMagicCodeDialogOpen(true);
		generateMagicCode();
	};

	const downloadBadge = () => {
		if (typeof window !== 'undefined') {
			const url = `/api/users/${profile.user.id}/badge`;
			window.open(url, '_blank');
		}
	};

	const handleOpenUserDeleteDialog = () => {
		setIsUserDeleteDialogOpen(true);
	};
	const handleCloseUserDeleteDialog = () => {
		setIsUserDeleteDialogOpen(false);
	};

	const resendInvite = async () => {
		setIsBusy(true);
		try {
			await request.post(`/api/invites/users/${profile.user.id}`, {});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to resend invite mail or text.',
			});
		}
		setIsUserInviteDialogOpen(false);
		setIsBusy(false);
		await revalidateUserProfile();
	};

	const sendInviteTextMessage = async () => {
		try {
			await request.post(`/api/invites/users/${profile.user.id}/sms`, {});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to send invite text.',
			});
		}

		setIsUserInviteTextMessageDialogOpen(false);
	};

	const reset2FA = async () => {
		try {
			await request.post(`/api/users/${profile.user.id}/reset-two-factor`, {});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to reset two-factor auth.',
			});
		}

		await revalidateUserProfile();
		set2FADialogOpen(false);
	};

	const handleOpenInviteUserTextMessageDialog = () => {
		setIsUserInviteTextMessageDialogOpen(true);
	};

	const handleCloseInviteUserTextMessageDialog = () => {
		setIsUserInviteTextMessageDialogOpen(false);
	};

	const handleOpenUserBlockDialog = () => {
		setIsUserBlockDialogOpen(true);
	};

	const handleRevalidateClick = async () => {
		let revalidatedUser: User | undefined;
		setIsBusy(true);
		try {
			revalidatedUser = (await request.put<User>(`/api/users/${profile.user.id}/check-blocklist`, {})).body;
			revalidateUserProfile();
		} catch (err: unknown) {
			const error = err as Error;
			EventEmitter.emit('toast', {
				type: 'error',
				message: `Update failed (${error.message})`,
			});
		}
		revalidateUsersPath();
		setIsBusy(false);

		if (!revalidatedUser) {
			return;
		}

		if (revalidatedUser?.status === 'blocked') {
			EventEmitter.emit('toast', {
				message: (
					<span>
						This user is still blocked. Please verify{' '}
						<Link to="/config/blocklist" className="underline" target="_blank">
							the blocklist
						</Link>{' '}
						to unblock.
					</span>
				),
				type: 'error',
			});
		}
	};

	const handleTabChange = (newValue: number) => {
		const tabId = tabs[newValue].id;
		navigate({ to: `/users/$userId/${tabId}`, params: { userId } });
	};

	const handleClose2FADialog = () => {
		set2FADialogOpen(false);
	};

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content>
					<GenericDetailPage.Content.Notifications>
						{isArchived && (
							<Alert severity="info" variant="filled" sx={{ marginBottom: 2 }}>
								This user is archived.
							</Alert>
						)}
						{isPlannedForArchive && !isArchived && (
							<Alert severity="info" variant="filled" sx={{ marginBottom: 2 }}>
								This user is scheduled for archival on{' '}
								{new Date(profile.user.plannedArchival!.archiveDate!).toLocaleDateString('en-GB')}. Re-inviting this
								user will cancel the scheduled archival.
							</Alert>
						)}
						{markedForReview.length > 0 && (
							<Alert severity="error" variant="filled" sx={{ marginBottom: 2, '& ul': { margin: 0, paddingLeft: 2 } }}>
								This user is currently locked and requires to be reviewed before receiving access.
								<ul>
									{isUnderageUser && <li>Underage user!</li>}
									{hasDocumentsToReview && <li>One or more documents are highlighted for review.</li>}
									{hasTasksToReview && <li>One or more tasks are highlighted for review.</li>}
									{hasAgeRule && <li>This user does not meet the age requirement.</li>}
									{hasNationalityRule && <li>User's document country is flagged.</li>}
									{hasScreeningReview && <li>Matches an international sanctions or watchlist source.</li>}
								</ul>
							</Alert>
						)}

						{isBlocked && (
							<Alert
								severity="error"
								variant="filled"
								action={
									<Button color="inherit" size="small" variant="outlined" onClick={handleRevalidateClick}>
										Revalidate
									</Button>
								}
								sx={{ marginBottom: 2, '& ul': { margin: 0, paddingLeft: 2 } }}
							>
								This user matches one or more blocklist rules or the user's identification document originates from a
								blocked country. Review and adapt{' '}
								<Link to="/config/blocklist" className="underline" target="_blank">
									the blocklist
								</Link>{' '}
								rules to re-enable access for this user.
							</Alert>
						)}
						{hasApprovalsTodo && (
							<Alert
								severity="warning"
								variant="filled"
								sx={{
									marginBottom: 2,
									'& ul': { margin: 0, paddingLeft: 2 },
								}}
							>
								This user has one or more approval tasks pending approval.
							</Alert>
						)}
					</GenericDetailPage.Content.Notifications>
					<Paper
						variant="outlined"
						sx={{
							display: 'flex',
							flexDirection: 'row',
						}}
					>
						<div className="flex w-[212px] justify-self-stretch p-2">
							<Avatar
								variant="square"
								sx={{
									width: 175,
									height: 200,
									borderRadius: 1,
									borderColor: 'action.disabledBackground',
									borderWidth: 1,
									borderStyle: 'solid',
								}}
								src={faceImage?.link ? `/api/users/${profile.user.id}/files/${faceImage?.link.split('/').pop()}` : ''}
							/>
						</div>
						<div className="p-2 w-full flex flex-col gap-2">
							<div className="flex items-center gap-2 justify-between">
								<div className="flex flex-col gap-1">
									<Typography variant="h6" mb={0.5}>
										{profile.user.firstName} {profile.user.lastName}
									</Typography>
								</div>
								<div className="flex items-center gap-2">
									{verifyPermission(PERMISSIONS.adminUsersEdit) && (
										<Button key="edit" variant="outlined" onClick={handleOpenUserEditDialog} startIcon={<EditIcon />}>
											Edit User
										</Button>
									)}
									{verifyPermission(PERMISSIONS.adminUsersArchive) && isArchived && (
										<Button key="restore" onClick={restoreUser} variant="outlined" color="info">
											Restore user
										</Button>
									)}
									{isActive && (
										<GenericDetailPage.Content.Header.Right.Menu>
											{verifyPermission(PERMISSIONS.adminUsersView) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item
													onClick={inviteUserPrompt}
													disabled={!profile.user.email && !profile.user.dispatcherRegistrationEmail}
												>
													<ListItemIcon>
														<MailIcon />
													</ListItemIcon>
													<ListItemText>Resend email invite</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminUsersView) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item
													onClick={handleOpenInviteUserTextMessageDialog}
													disabled={!profile.user.phone}
												>
													<ListItemIcon>
														<PhoneAndroidIcon />
													</ListItemIcon>
													<ListItemText>Send magic text message</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminUsersView) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={magicCodePrompt}>
													<ListItemIcon>
														<AutoFixHighIcon />
													</ListItemIcon>
													<ListItemText>Generate magic code</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminUsersArchive) && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={handleOpenUserDeleteDialog}>
													<ListItemIcon>
														<DeleteIcon />
													</ListItemIcon>
													<ListItemText>Archive user</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminUsersArchive) && isPlannedForArchive && !isArchived && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={cancelPlannedArchival}>
													<ListItemIcon>
														<CancelIcon />
													</ListItemIcon>
													<ListItemText>Cancel planned archival</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminSecurityBlock) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={handleOpenUserBlockDialog}>
													<ListItemIcon>
														<BlockIcon />
													</ListItemIcon>
													<ListItemText>Block user</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminUsersReview) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={toggleReview}>
													<ListItemIcon>
														<ReportIcon />
													</ListItemIcon>
													<ListItemText>
														{markedForReview.length > 0 ? 'Mark reviewed' : 'Mark for review'}
													</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{verifyPermission(PERMISSIONS.adminSecurityBadgePrinting) && !isBlocked && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={downloadBadge}>
													<ListItemIcon>
														<BadgeIcon />
													</ListItemIcon>
													<ListItemText>Download badge</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
											{profile.user?.hasTwoFactor && verifyPermission(PERMISSIONS.siteAdmin) && (
												<GenericDetailPage.Content.Header.Right.Menu.Item onClick={() => set2FADialogOpen(true)}>
													<ListItemIcon>
														<LockIcon />
													</ListItemIcon>
													<ListItemText>Reset 2FA</ListItemText>
												</GenericDetailPage.Content.Header.Right.Menu.Item>
											)}
										</GenericDetailPage.Content.Header.Right.Menu>
									)}
								</div>
							</div>
							<div className="flex flex-row flex-wrap">
								<div className="flex-grow min-w-[200px]">
									<Typography variant="caption" sx={{ marginBottom: 1 }}>
										Email
									</Typography>
									<Typography variant="subtitle2" sx={{ marginBottom: 1, minHeight: 24 }}>
										{profile.user.email || '-'}
									</Typography>
									{profile.user?.phone && (
										<>
											<Typography variant="caption" sx={{ marginBottom: 1 }}>
												Phone
											</Typography>
											<Typography variant="subtitle2" sx={{ marginBottom: 1, minHeight: 24 }}>
												{formatPhoneNumber(profile.user.phone)}
											</Typography>
										</>
									)}
									<Typography variant="caption" mb={1} mt={2}>
										Companies
									</Typography>
									<div className="flex flex-wrap gap-1">
										{profile.user.groups.map((group) => (
											<GroupChip
												key={group.id}
												group={group}
												onDelete={
													verifyPermission(PERMISSIONS.adminGroupsEdit) ? () => handleDeleteGroup(group.id) : undefined
												}
											/>
										))}
										{verifyPermission(PERMISSIONS.adminGroupsEdit) && (
											<Badge onClick={() => setIsGroupDialogOpen(true)}>+</Badge>
										)}
									</div>
								</div>
								<div className="flex-grow min-w-[200px]">
									{faceImage && (
										<>
											<Typography variant="caption" sx={{ marginBottom: 1 }}>
												FacePrint
											</Typography>
											<Typography variant="subtitle2" sx={{ marginBottom: 1, minHeight: 24 }}>
												<FaceChip facePrint={profile.user.facePrintType} />
											</Typography>
										</>
									)}
									<Typography variant="caption" mb={1} mt={faceImage ? 2 : 0}>
										Roles
									</Typography>
									<div className="flex flex-wrap gap-1">
										{profile.user.roles.map((r) => (
											<RoleChip
												key={r.id}
												role={r}
												userMarkedForReview={profile.user.markedForReview || profile.user.status === 'blocked'}
											/>
										))}
									</div>
									{showSupervisorAssignment && (
										<div className="pt-2">
											<Typography variant="caption" mb={1}>
												Supervisor
											</Typography>
											<div className="flex flex-wrap gap-1">
												{profile.user.supervisor && (
													<SupervisorChip
														supervisor={profile.user.supervisor}
														onDelete={
															verifyPermission(PERMISSIONS.adminUsersEdit)
																? async () => {
																		try {
																			await request.del(`/api/users/${profile.user.id}/supervisor`);
																			EventEmitter.emit('toast', { type: 'success', message: 'Supervisor removed' });
																			revalidateUserProfile();
																		} catch (_err) {
																			EventEmitter.emit('toast', {
																				type: 'error',
																				message: 'Error removing supervisor',
																			});
																		}
																	}
																: undefined
														}
													/>
												)}
												{!profile.user.supervisor && verifyPermission(PERMISSIONS.adminUsersEdit) && (
													<Badge onClick={() => setIsSupervisorDialogOpen(true)}>+</Badge>
												)}
											</div>
										</div>
									)}
									{usesRFID && verifyPermission(PERMISSIONS.adminSecurityAccessmanagement) && (
										<Fragment>
											<Typography variant="caption" sx={{ marginBottom: 1 }}>
												Badge ID
											</Typography>
											<div>
												<Badge
													onClick={
														verifyPermission(PERMISSIONS.adminSecurityAccessmanagement) ? handleUpdateBadge : undefined
													}
												>
													<ContactlessIcon />
													{toFormattedHex(profile.user.badgeId)}
												</Badge>
											</div>
										</Fragment>
									)}
									{!usesRFID && profile.user.badgeId && (
										<Fragment>
											<Typography variant="caption" sx={{ marginBottom: 1 }}>
												Badge ID
											</Typography>
											<div>
												<Badge>{profile.user.badgeId}</Badge>
											</div>
										</Fragment>
									)}
								</div>
								<div className="flex-grow min-w-[200px] overflow-auto">
									{showShortId && (
										<>
											<Typography variant="caption" sx={{ marginBottom: 1 }}>
												Short ID
											</Typography>
											<Typography variant="subtitle2" sx={{ marginBottom: 1, minHeight: 24 }}>
												{profile.user.shortId}
											</Typography>
										</>
									)}

									{extraPropFields.map((field) => {
										return (
											<Fragment key={field.name}>
												<Typography variant="caption" sx={{ marginBottom: 1 }}>
													{field.name}
												</Typography>
												<div>
													<EditableChip
														value={
															(profile.user.userData?.extraUserProps as Record<string, unknown> | undefined)?.[
																field.name
															] as string | undefined
														}
														onChange={(value) => handleExtraPropUpdate(field.name, value)}
														disabled={!verifyPermission(PERMISSIONS.adminUsersEdit)}
													/>
												</div>
											</Fragment>
										);
									})}
								</div>
							</div>
							{/* BASF badge slot - custom integration, separate from the main app */}
							{isBasf && (
								<Suspense fallback={null}>
									<BasfUserBadgeSlot
										profile={profile}
										verifyPermission={verifyPermission}
										notify={(level, message) => EventEmitter.emit('toast', { type: level, message })}
										onChanged={revalidateUserProfile}
									/>
								</Suspense>
							)}
						</div>
					</Paper>
				</GenericDetailPage.Content>
				<Tabs value={tabIndexPerTabId[selectedLayoutSegment] ?? 0} onChange={handleTabChange} className="px-4">
					<Tabs.TabList>
						{tabs.map((tab, index) => (
							<Tabs.Tab
								key={tab.id}
								value={index}
								label={
									<div className="flex items-center gap-2">
										{tab.label}
										{tab.error && <span className="inline-flex h-2 w-2 rounded-full bg-red-600" />}
									</div>
								}
							/>
						))}
					</Tabs.TabList>
				</Tabs>
				<Outlet />
			</GenericDetailPage>
			<AddToGroupDialog
				isOpen={isGroupDialogOpen}
				onClose={() => setIsGroupDialogOpen(false)}
				onReload={() => {
					revalidateUserProfile();
				}}
				userId={profile.user.id}
			/>
			{showSupervisorAssignment && (
				<AssignSupervisorDialog
					isOpen={isSupervisorDialogOpen}
					onClose={() => setIsSupervisorDialogOpen(false)}
					onReload={revalidateUserProfile}
					userId={profile.user.id}
					currentSupervisorId={profile.user.supervisorId}
				/>
			)}
			<BlockUserForm isOpen={isUserBlockDialogOpen} onClose={handleCloseBlockUserDialog} onSubmit={blockUser} />
			<FormDialog
				isOpen={isUserDeleteDialogOpen}
				onClose={handleCloseUserDeleteDialog}
				title="Archive User"
				description="Are you sure you want to archive this user?"
				buttons={
					<>
						<Button variant="outlined" onClick={handleCloseUserDeleteDialog}>
							Cancel
						</Button>
						<Button variant="contained" color="error" disabled={!archiveIsValid} onClick={() => archiveUser()}>
							Archive
						</Button>
					</>
				}
			>
				<Tooltip
					title="The user will be archived on the selected date at 00:00 AM. If today is selected, the user will be archived immediately."
					placement="bottom-start"
					PopperProps={{
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, -12],
								},
							},
						],
					}}
				>
					<div
						style={{
							marginTop: 16,
							display: 'flex',
							alignItems: 'center',
							gap: 8,
						}}
					>
						<DialogContentText sx={{ mb: 0 }}>User will be archived on:</DialogContentText>
						<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
							<DatePicker
								value={archiveDate}
								onChange={(newDate) => newDate && setArchiveDate(newDate)}
								minDate={new Date()}
								slotProps={{
									textField: {
										size: 'small',
									},
								}}
							/>
						</LocalizationProvider>
					</div>
				</Tooltip>
			</FormDialog>
			<MagicCodeDialog
				isOpen={isMagicCodeDialogOpen}
				onClose={() => {
					setIsMagicCodeDialogOpen(false);
				}}
				magicCode={magicCode}
			/>
			<ConfirmDialog
				isOpen={isUserInviteDialogOpen}
				cancelAction={closeInviteUserPrompt}
				confirmAction={resendInvite}
				cancelLabel="Cancel"
				confirmLabel="Resend Invite"
				confirmDisabled={isBusy}
			>
				Are you sure you want to resend an invite email or text message to this user?
			</ConfirmDialog>
			<ConfirmDialog
				isOpen={isUserInviteTextMessageDialogOpen}
				cancelAction={handleCloseInviteUserTextMessageDialog}
				confirmAction={sendInviteTextMessage}
				cancelLabel="Cancel"
				confirmLabel="Send "
			>
				Are you sure you want to send a magic text message to this user?
			</ConfirmDialog>

			<ConfirmDialog
				isOpen={is2FADialogOpen}
				cancelAction={handleClose2FADialog}
				confirmAction={reset2FA}
				cancelLabel="Cancel"
				confirmLabel="Reset"
			>
				Are you sure you want to reset this user's two-factor authentication?
			</ConfirmDialog>

			{isBadgeDialogOpen && (
				<BadgeDialog
					isOpen={isBadgeDialogOpen}
					originalBadgeId={profile.user.badgeId}
					onSave={saveBadgeId}
					onDelete={deleteBadgeId}
					onClose={handleCloseBadgeDialog}
				/>
			)}
			<UserForm
				isOpen={isUserEditDialogOpen}
				onClose={handleCloseUserEditDialog}
				onSubmit={updateUserData}
				data={profile.user}
				isAdmin={verifyPermission(['*']) || verifyPermission(PERMISSIONS.adminUsersEditEmail)}
				authenticatedUser={authenticatedUser}
				defaultCountry={defaultCountry}
			/>
		</>
	);
}
